import { FC } from "react";
import Box from "@mui/material/Box";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

/** The shared "AI" gradient — used by the AI badge (menu rows / section head). */
export const AI_GRADIENT =
  "linear-gradient(135deg, #6366F1 0%, #8B5CF6 45%, #EC4899 100%)";

/**
 * Distinct fills so multiple active models are tellable apart. Each chip fills
 * with a self-gradient (base → a lighter shade of the same hue) and gets a soft
 * glow in the base color — no border. `glow` is the base color as `r, g, b`.
 */
const AI_FILLS = [
  { from: "#2563EB", to: "#60A5FA", glow: "37, 99, 235" }, // blue
  { from: "#0D9488", to: "#2DD4BF", glow: "13, 148, 136" }, // teal
  { from: "#DB2777", to: "#F472B6", glow: "219, 39, 119" }, // rose
];

/** Chip fill gradient for the Nth active model — its base color eased into a
 * lighter shade of itself. White text on top. */
export function aiFillGradient(index = 0): string {
  const f = AI_FILLS[Math.abs(index) % AI_FILLS.length];
  return `linear-gradient(135deg, ${f.from} 0%, ${f.to} 100%)`;
}

/** Colored glow (box-shadow) for the Nth active model — replaces a border. */
export function aiGlow(index = 0): string {
  const f = AI_FILLS[Math.abs(index) % AI_FILLS.length];
  return `0 0 2px rgba(${f.glow}, 0.7), 0 0 8px rgba(${f.glow}, 0.55)`;
}

interface AiBadgeProps {
  /** Badge text. Defaults to "AI". */
  label?: string;
  /** `sm` for chips/pills/menu rows, `md` for section headers. */
  size?: "sm" | "md";
}

/**
 * Modern gradient "AI-powered" badge — a sparkle + gradient tag. Used for the
 * picker menu rows and section headers to mark new (argo) scoring models.
 */
export const AiBadge: FC<AiBadgeProps> = ({ label = "AI", size = "sm" }) => {
  const md = size === "md";
  return (
    <Box
      component="span"
      data-testid="scoring-ai-badge"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.25,
        px: md ? 0.85 : 0.5,
        height: md ? 20 : 15,
        borderRadius: 999,
        fontSize: md ? 10 : 10,
        fontWeight: 700,
        letterSpacing: 0.3,
        lineHeight: 1,
        textTransform: "uppercase",
        color: "#fff",
        background: AI_GRADIENT,
        boxShadow: md ? "0 1px 5px rgba(139, 92, 246, 0.45)" : "none",
        flexShrink: 0,
      }}
    >
      <AutoAwesomeIcon sx={{ fontSize: md ? 12 : 10 }} />
      {label}
    </Box>
  );
};
